import React, { useEffect, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";

import { T } from "../constants";
import { useUi, cx } from "../ui";
import Overlay from "./Overlay";
import { formatMoney } from "../helpers";
import api from "../../../lib/api";
import { useAppConfig } from "../../../components/AppConfigContext";

const CARD_OVERLAY_Z = 1400;

const useStyles = makeStyles({
  dialog: {
    width: "100%",
    maxWidth: 440,
    borderRadius: 12,
    border: `1px solid ${T.border}`,
    background: T.elevated,
    padding: 24,
    boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
  },
  heading: { margin: "2px 0 14px", fontSize: 20, fontWeight: 700, color: T.fg },
  sub: { margin: "0 0 16px", fontSize: 12, color: T.mutedFg },
  error: {
    margin: "0 0 12px",
    borderRadius: 6,
    border: `1px solid ${T.danger}`,
    background: T.dangerTint,
    padding: "8px 10px",
    fontSize: 12,
    color: T.fg,
  },
  footer: { marginTop: 20, display: "flex", justifyContent: "flex-end", gap: 8 },
  cancelBtn: { border: "none", background: "transparent" },
  unavailable: { margin: 0, fontSize: 13, color: T.mutedFg },
});

function setupErrorMessage(err) {
  const code = err?.data?.error ?? err?.response?.data?.error ?? null;

  if (code === "PAY_LATER_NOT_ENABLED") {
    return "Card payment isn't enabled for this booking. Take payment at the desk.";
  }

  return "Unable to start card setup. Please try again.";
}

function AddCardForm({
  bookingId,
  amountDue,
  currency,
  onClose,
  onConfirmed,
  onProcessingChange,
}) {
  const s = useStyles();
  const ui = useUi();
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  /* Never leave the parent latched busy if this form unmounts mid-confirm. */
  useEffect(() => () => onProcessingChange(false), [onProcessingChange]);

  function stopLoading() {
    setLoading(false);
    onProcessingChange(false);
  }

  async function submit() {
    /* The processing lock: a second submit mid-confirm would strand a
     * SetupIntent and could take the guest's card twice. */
    if (!stripe || !elements || loading) return;

    setLoading(true);
    onProcessingChange(true);
    setError("");

    const { error: submitError } = await elements.submit();

    if (submitError) {
      setError("Please enter valid card details.");
      stopLoading();
      return;
    }

    let clientSecret;

    try {
      ({ client_secret: clientSecret } = await api.createValetSetupIntent(
        bookingId,
      ));
    } catch (err) {
      /* Without this catch the modal stays stuck on the spinner and the
       * rejection surfaces as an unhandled promise error. */
      setError(setupErrorMessage(err));
      stopLoading();
      return;
    }

    const { error: confirmError } = await stripe.confirmSetup({
      elements,
      clientSecret,
      redirect: "if_required",
      confirmParams: { return_url: window.location.href },
    });

    if (confirmError) {
      setError("Invalid payment method. Please enter valid card details.");
      stopLoading();
      return;
    }

    try {
      await api.confirmValetCard(bookingId);
    } catch (err) {
      setError("Could not save that card. Please try again.");
      stopLoading();
      return;
    }

    /* Left loading: the caller charges straight away, and re-enabling the
     * button in between only invites a second tap. */
    onConfirmed();
  }

  return (
    <div>
      {error && <div className={s.error}>{error}</div>}

      <PaymentElement />

      <div className={s.footer}>
        <button
          onClick={onClose}
          disabled={loading}
          className={cx(ui.mutedBtn, s.cancelBtn)}
        >
          Cancel
        </button>
        <button onClick={submit} disabled={loading} className={ui.primaryBtn}>
          {loading
            ? "Processing…"
            : `Add card & charge ${formatMoney(amountDue, currency)}`}
        </button>
      </div>
    </div>
  );
}

export default function AddCardModal({
  bookingId,
  amountDue,
  currency = "AUD",
  country,
  onClose,
  onConfirmed,
}) {
  const s = useStyles();
  const ui = useUi();
  const appConfig = useAppConfig();

  const [processing, setProcessing] = useState(false);

  /* Per-country publishable key, falling back to AU. */
  const countryUpper = country ? country.toUpperCase() : null;
  const publishableKey =
    (countryUpper === "US" && appConfig?.STRIPE_PUBLISHABLE_KEY_US) ||
    (countryUpper === "NZ" && appConfig?.STRIPE_PUBLISHABLE_KEY_NZ) ||
    appConfig?.STRIPE_PUBLISHABLE_KEY;

  /* Memoised so <Elements> never remounts mid-payment and wipes in-progress
   * card entry. */
  const stripeInstance = useMemo(
    () => (publishableKey ? loadStripe(publishableKey) : null),
    [publishableKey],
  );
  const options = useMemo(
    () => ({ mode: "setup", currency: (currency || "AUD").toLowerCase() }),
    [currency],
  );

  return (
    <Overlay onClose={processing ? undefined : onClose} z={CARD_OVERLAY_Z}>
      <div className={s.dialog}>
        <div className={ui.eyebrow}>Payment</div>
        <h3 className={s.heading}>Add card details</h3>

        {stripeInstance ? (
          <Elements stripe={stripeInstance} options={options}>
            <AddCardForm
              bookingId={bookingId}
              amountDue={amountDue}
              currency={currency}
              onClose={onClose}
              onConfirmed={onConfirmed}
              onProcessingChange={setProcessing}
            />
          </Elements>
        ) : (
          <div>
            <p className={s.unavailable}>
              Card payment isn&apos;t configured for this site. Take payment at
              the desk.
            </p>
            <div className={s.footer}>
              <button onClick={onClose} className={ui.mutedBtn}>
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </Overlay>
  );
}
